import _ from "lodash";
import { v4 as uuidV4 } from "uuid";
import { type Actor, type SnapshotFrom, createActor, fromPromise, waitFor } from "xstate";

import { type MetadataOperation, type PageInfo } from "../api-canvas-metadata";
import {
	type CanvasSpecifier,
	type DocumentMetadata,
	makePingMessage,
	metadataMessageActiveUsersBroadcast,
	metadataMessageTransactionBroadcast,
	pongMessage,
} from "../api-types";
import { safeParse, safeStringify } from "../json";
import { type LoggerInterface } from "../logger-types";
import { MetadataCollabApiClient } from "../metadata-collab-api-client";
import {
	archiveCanvasOperation,
	archiveCanvasTransaction,
	archivePageOperation,
	archivePageTransaction,
	createPageOperation,
	createPageTransaction,
	duplicatePageOperation,
	duplicatePageTransaction,
	setCanvasTemplateOperation,
	setCanvasTemplateTransaction,
	setCanvasTitleOperation,
	setCanvasTitleTransaction,
	setPageOrderOperation,
	setPageOrderTransaction,
	setPageTitleOperation,
	setPageTitleTransaction,
} from "../metadata-state";
import {
	ERROR_NO_ACTIVE_TRANSACTION,
	type EnqueueTransactionOptions,
	type SubmitTransactionInput,
	type SubmitTransactionOutput,
	makeStateSyncClientMachine,
	waitForTransactionProcessed,
} from "../state-sync-client";
import { NoopStateStorage } from "./NoopStateStorage";
import { type StateStorage } from "./StateStorage";
import { type Transaction } from "./Transaction";

/**
 * MetadataRoomClient is a wrapper interface for the subset of the PartSocket interface the protocol
 * uses. We don't want to take a dependency on PartySocket in this package, so this is enough
 */
export interface MetadataRoomClient {
	addEventListener: (
		type: "message",
		callback: (event: MessageEvent) => void,
		options?: boolean | AddEventListenerOptions | undefined
	) => void;
	removeEventListener: (type: "message", callback: (event: MessageEvent) => void) => void;
	id: string;
	close: () => void;
	send: (message: string | ArrayBuffer | Blob | ArrayBufferView) => void;
}

export class MetadataClient {
	/** Our link to the metadata server, usually hosted as a Cloudflare Durable Object. */
	private _client: MetadataRoomClient;
	private _metadataApiClient: MetadataCollabApiClient;

	_clientMachine: Actor<ReturnType<typeof makeStateSyncClientMachine<DocumentMetadata>>>;
	_storage: StateStorage<DocumentMetadata>;
	private inFlightPingIDs = new Set<string>();
	private _pingInterval?: ReturnType<typeof setInterval>;
	/**
	 * The event handler we use to subscribe to transaction broadcasts. Stored here so we can call
	 * `removeEventListener` on `dispose`.
	 */
	private _subscriptionHandler: (event: MessageEvent) => void;

	private _offlineHandler = () => {
		this._clientMachine.send({ type: "connectionFailureDetected" });
	};

	private _onlineHandler = () => {
		this._clientMachine.send({ type: "connectionRestored" });
	};

	private _logger: LoggerInterface;

	constructor(opts: {
		client: MetadataRoomClient;
		storage?: StateStorage<DocumentMetadata>;
		fetch: (init?: RequestInit | undefined) => Promise<Response>;
		logger: LoggerInterface;
		pingIntervalMs?: number;
	}) {
		this._client = opts.client;
		this._storage = opts.storage ?? new NoopStateStorage();
		this._logger = opts.logger;
		this._metadataApiClient = new MetadataCollabApiClient(opts.fetch);

		this._clientMachine = createActor(
			makeStateSyncClientMachine<DocumentMetadata>({
				connect: fromPromise(async ({ input }) => {
					const response = await this._metadataApiClient.initMetadataRoom(
						input.extra?.["canvasSpecifier"]
					);

					return { version: response.version, data: response.canvasMetadata };
				}),
				submitTransaction: fromPromise<
					SubmitTransactionOutput,
					SubmitTransactionInput<DocumentMetadata>
				>(async ({ input: activeTransaction }) => {
					if (!activeTransaction) {
						throw ERROR_NO_ACTIVE_TRANSACTION;
					}
					const operation = activeTransaction.extras?.["operation"];
					const transactionId = activeTransaction.id ?? uuidV4();
					return await this._metadataApiClient.enqueueTransaction(
						transactionId,
						activeTransaction.lastVersionSeen,
						operation
					);
				}),
				fetchMissingTransactions: fromPromise<unknown, { version: number }>(
					async ({ input }) => {
						return await this._metadataApiClient.stepsSince(input.version);
					}
				),
			})
		);
		this._clientMachine.start();

		// FIXME: We do not know how to detect that the Partysocket has dropped, so we just check
		// whether the connection is offline generally.
		window.addEventListener("offline", this._offlineHandler);
		window.addEventListener("online", this._onlineHandler);
		this._subscriptionHandler = async (event: { data: unknown }) => {
			if (typeof event.data !== "string") {
				this._logger.error("unexpected message type from websocket connection", {
					message: event.data,
				});
				return;
			}
			try {
				const data = safeParse(event.data, this._logger);
				switch (data?.["type"]) {
					case "transaction-broadcast": {
						const transactionBroadcastMessage =
							metadataMessageTransactionBroadcast.parse(data);
						this._clientMachine.send({
							type: "receiveServerReplicaUpdate",
							replicaVersion:
								transactionBroadcastMessage.transactionProcessedEvent.transaction
									.lastVersionSeen,
							patches:
								transactionBroadcastMessage.transactionProcessedEvent.transaction
									.patches,
						});
						break;
					}
					case "active-users-broadcast": {
						const activeUsersBroadcast =
							metadataMessageActiveUsersBroadcast.parse(data);
						const snap = this._clientMachine.getSnapshot();
						const isUnchanged = _.isEqual(
							snap.context.activeUsers,
							activeUsersBroadcast.activeUsers
						);

						if (isUnchanged) {
							return;
						}

						// TODO figure out why this doesn't always get received by the state machine on first load
						this._clientMachine.send({
							type: "activeUsersUpdated",
							activeUsers: activeUsersBroadcast.activeUsers,
						});
						break;
					}
					case "disconnect": {
						this._logger.info("metadata collab server sending a disconnect signal", {
							data,
						});
						break;
					}
					case "pong": {
						const pong = pongMessage.parse(data);
						this.acknowledgedPong(pong.uuid);
						break;
					}
					case "persistence-error": {
						this._logger.error("received a persistence error from the server", {
							data: safeStringify(data),
						});
						this._clientMachine.send({ type: "persistenceError" });
						break;
					}
					default:
						// Ignore anything that's not transaction broadcast.
						return;
				}
			} catch (error) {
				this._logger.error("unable to complete broadcast event", {
					data: event.data,
					error,
				});
			}
		};
		this._client.addEventListener("message", this._subscriptionHandler);
		if (opts.pingIntervalMs) {
			this._pingInterval = setInterval(() => {
				this.sendPing();
			}, opts.pingIntervalMs);
		}
	}

	sendPing(isDebug = false) {
		if (this.inFlightPingIDs.size != 0) {
			// This shouldn't happen if connections are healthy.
			this._logger.info(
				"[metadata_client] found unacknowledged pings upon acknowledge completion",
				{
					inFlightPingIDs: this.inFlightPingIDs,
				}
			);
		}
		try {
			const ping = makePingMessage(isDebug);
			if (isDebug) {
				this._logger.debug("[metadata_client] sending ping", {
					ping,
				});
			}
			this.inFlightPingIDs.add(ping.uuid);
			this._client.send(safeStringify(ping));
		} catch (error) {
			this._logger.error("[metadata_client] ping request failed due to error", {
				error,
			});
		}
	}

	/** Initializes the canvas room. */
	async idempotentInitMetadataRoom(
		canvasSpecifier: CanvasSpecifier
	): ReturnType<typeof waitFor<MetadataClient["_clientMachine"]>> {
		const waiter = waitFor(this._clientMachine, (snap) => snap.matches("connected"));
		this._clientMachine.send({ type: "connect", extra: { canvasSpecifier } });

		const snap = await waiter;
		return snap;
	}

	async renamePage(pageId: string, title: string) {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = setPageTitleTransaction(metadataSnapshot, pageId, title);
		const operation = setPageTitleOperation(pageId, title);
		return this.commit(transaction, operation);
	}

	async setTitle(title: string) {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = setCanvasTitleTransaction(metadataSnapshot, title);
		const operation = setCanvasTitleOperation(title);
		return this.commit(transaction, operation);
	}

	async setArchived(archived: boolean) {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = archiveCanvasTransaction(metadataSnapshot, archived);
		const operation = archiveCanvasOperation(archived);
		return this.commit(transaction, operation);
	}

	async setTemplate(isTemplate: boolean) {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = setCanvasTemplateTransaction(metadataSnapshot, isTemplate);
		const operation = setCanvasTemplateOperation(isTemplate);
		return this.commit(transaction, operation);
	}

	async archivePage(pageId: string, archived: boolean) {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = archivePageTransaction(metadataSnapshot, pageId, archived);
		const operation = archivePageOperation(pageId, archived);
		return await this.commit(transaction, operation);
	}

	async createPage(authorId: string, title = "") {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = createPageTransaction(metadataSnapshot, authorId, title);
		const operation = createPageOperation(authorId, title);
		return this.commit(transaction, operation, { avoidOptimisticUpdate: true });
	}

	async setPageOrder(newPageIds: PageInfo[]) {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = setPageOrderTransaction(metadataSnapshot, newPageIds);
		const operation = setPageOrderOperation(newPageIds);
		return this.commit(transaction, operation);
	}

	async duplicatePage(pageId: string, authorId: string) {
		const metadataSnapshot = this._clientMachine.getSnapshot().context.localData.data;
		const transaction = duplicatePageTransaction(metadataSnapshot, authorId, pageId);
		const operation = duplicatePageOperation(authorId, pageId);
		return this.commit(transaction, operation, { avoidOptimisticUpdate: true });
	}

	acknowledgedPong(uuid: string) {
		if (this.inFlightPingIDs.has(uuid)) {
			this.inFlightPingIDs.delete(uuid);
		} else {
			this._logger.debug("attempted to acknowledge a non-existent uuid from a pong", {
				unknownID: uuid,
				inFlightPingIDs: this.inFlightPingIDs,
			});
		}
	}

	subscribe<U>(
		onUpdate: (t: DocumentMetadata, snap: SnapshotFrom<typeof this._clientMachine>) => U
	) {
		// TODO: This hook fires on every state transition, which is what we want, but there's also
		// no way (outside React hooks) to memoize it. We should probably allow for this case.
		return this._clientMachine.subscribe((snap) => {
			return onUpdate(snap.context.localData.data, snap);
		});
	}

	dispose() {
		clearInterval(this._pingInterval);
		this._client.removeEventListener("message", this._subscriptionHandler);
		window.removeEventListener("offline", this._offlineHandler);
		window.removeEventListener("online", this._onlineHandler);
		this._clientMachine.stop();
	}

	private async commit(
		transaction: Transaction<DocumentMetadata>,
		operation: MetadataOperation,
		options?: EnqueueTransactionOptions
	): ReturnType<typeof waitForTransactionProcessed<DocumentMetadata>> {
		const snap = this._clientMachine.getSnapshot();
		if (!snap.matches({ connected: { editableStatus: "enabled" } })) {
			throw new Error(
				`state machine is not accepting transactions. current machine state: ${snap.value}`
			);
		}
		const resolvedOptions: EnqueueTransactionOptions = {
			avoidOptimisticUpdate: options?.avoidOptimisticUpdate ?? false,
			extras: {
				operation,
			},
		};
		const transactionId = uuidV4();
		this._clientMachine.send({
			type: "enqueueTransaction",
			transactionId,
			patches: transaction.patches,
			options: resolvedOptions,
		});
		return await waitForTransactionProcessed(this._clientMachine, transactionId);
	}
}
